import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { ChevronRight, ArrowLeft, Zap, Star, Trophy, Target, Award, ListChecks, LayoutGrid, TrendingUp } from 'lucide-react';

/* ─── HELPERS ─── */
const colorPuesto = (puesto) => {
  if (!puesto || puesto === 'NO') return 'text-red-500 bg-red-500/10 border-red-500/30';
  if (puesto.startsWith('1')) return 'text-yellow-400 bg-yellow-400/10 border-yellow-500/30';
  if (puesto.startsWith('2')) return 'text-slate-300 bg-slate-300/10 border-slate-400/30';
  if (puesto.startsWith('3')) return 'text-orange-500 bg-orange-500/10 border-orange-500/30';
  if (puesto.startsWith('4')) return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
  if (puesto.startsWith('5')) return 'text-purple-400 bg-purple-400/10 border-purple-400/30';
  return 'text-slate-500 bg-slate-500/10 border-slate-500/30';
};

const puestoBadge = (puesto) => {
  if (!puesto) return '—';
  if (puesto === 'NO') return 'NO';
  return puesto.replace('P', '°');
};

const estadoColor = (estado) => {
  if (!estado) return 'bg-slate-800 text-slate-400';
  const e = estado.toLowerCase();
  if (e.includes('banco')) return 'bg-green-600 text-white';
  if (e.includes('confianza')) return 'bg-blue-600 text-white';
  if (e.includes('especulativo')) return 'bg-yellow-600 text-white';
  return 'bg-slate-800 text-slate-400';
};

const diferenciaColor = (diff) => {
  if (diff > 5) return 'text-green-400 bg-green-400/10';
  if (diff > 1) return 'text-lime-400 bg-lime-400/10';
  if (diff > 0) return 'text-yellow-400 bg-yellow-400/10';
  if (diff === 0) return 'text-slate-400 bg-slate-400/10';
  if (diff > -5) return 'text-orange-400 bg-orange-400/10';
  return 'text-red-400 bg-red-400/10';
};

/* ─── COMPONENTE PRINCIPAL ─── */
export default function AnalysisView({ jornadasData }) {
  const [jornadas, setJornadas] = useState([]);
  const [hipodromoSel, setHipodromoSel] = useState(null);
  const [carreraSel, setCarreraSel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const procesarJornadas = (data) => {
      if (!data) return [];
      const listaOriginal = Array.isArray(data) ? data : [data];
      return listaOriginal.map(j => ({
        ...j,
        fechaNormalizada: j.fecha ? j.fecha.trim().replace(/\//g, '-') : ''
      })).sort((a, b) => b.fechaNormalizada.localeCompare(a.fechaNormalizada));
    };

    if (jornadasData && (Array.isArray(jornadasData) ? jornadasData.length > 0 : Object.keys(jornadasData).length > 0)) {
      setJornadas(procesarJornadas(jornadasData));
      setLoading(false);
    } else {
      axios.get(`${API_URL}/api/jornadas-hoy`)
        .then(res => {
          console.log('Jornadas desde API:', res.data);
          setJornadas(procesarJornadas(res.data));
          setLoading(false);
        })
        .catch(err => {
          console.error('Error al cargar jornadas:', err);
          setLoading(false);
        });
    }
  }, [jornadasData]);

  if (loading) return <div className="p-10 text-center text-yellow-500 animate-pulse font-black uppercase italic tracking-tighter">Conectando con TurfMaster...</div>;

  /* ─── PANTALLA DE SELECCIÓN DE JORNADAS ─── */
  if (!hipodromoSel) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-black text-white mb-4 sm:mb-6 uppercase tracking-wider border-b-2 border-blue-600 pb-2 italic">Jornadas Disponibles</h2>
        <div className="grid gap-3 sm:gap-4">
          {jornadas.map((j, idx) => (
            <button key={idx} onClick={() => { setHipodromoSel(j); setCarreraSel(null); }}
              className="bg-slate-900 hover:bg-blue-800 p-4 sm:p-6 rounded-2xl flex justify-between items-center transition-all border border-yellow-900 group shadow-2xl active:scale-95">
              <div className="text-left">
                <span className="block text-white font-black text-lg sm:text-2xl uppercase italic leading-none">{j.hipodromo}</span>
                <span className="text-blue-400 text-[10px] font-bold uppercase">{j.fecha}</span>
              </div>
              <ChevronRight className="text-blue-600 group-hover:translate-x-2 transition-transform" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ─── DATOS DEL RESUMEN DE ORO ─── */
  const resumenOro = hipodromoSel.resumen_de_oro || {};
  const fijos = resumenOro.los_3_fijos || [];
  const tablaMaestra = resumenOro.tabla_maestra || [];
  const estrategia = resumenOro.estrategia_multicarrera || {};
  const topPuestos = resumenOro.top_3_oportunidades_puestos || [];

  return (
    <div className="p-4 max-w-10xl mx-auto text-white">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => {setHipodromoSel(null); setCarreraSel(null);}} className="flex items-center gap-2 text-blue-500 font-black text-[10px] uppercase hover:text-white transition-colors">
          <ArrowLeft size={16} /> Volver
        </button>
        <div className="text-right">
          <h1 className="text-3xl font-black italic uppercase text-white tracking-tighter leading-none">{hipodromoSel.hipodromo}</h1>
          <span className="text-blue-500 font-bold text-[10px]">{hipodromoSel.fecha}</span>
        </div>
      </div>

      {/* NAVEGACIÓN */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
        <div className="flex gap-2 py-1 overflow-x-auto no-scrollbar pb-2 sm:pb-0">
          {hipodromoSel.paneles_carreras?.map((c, idx) => (
            <button key={idx} onClick={() => setCarreraSel(c)}
              className={`flex-shrink-0 w-12 h-12 sm:w-11 sm:h-11 rounded-xl font-black text-lg transition-all border-2 touch-manipulation ${
                carreraSel?.carrera_num === c.carrera_num
                  ? 'bg-yellow-500 border-yellow-200 text-black scale-110 shadow-lg'
                  : 'bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-600'
              }`}>
              {c.carrera_num}
            </button>
          ))}
        </div>
        <button onClick={() => setCarreraSel(null)}
          className={`px-4 sm:px-6 h-12 sm:h-11 rounded-xl font-black text-xs uppercase transition-all border-2 flex items-center gap-2 touch-manipulation ${
            carreraSel === null
              ? 'bg-blue-600 border-blue-400 text-white shadow-lg'
              : 'bg-slate-900 border-slate-800 text-blue-500'
          }`}>
          <Trophy size={16} /> <span className="hidden sm:inline">Resumen Oro</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {carreraSel === null ? (
          /* ════════════════════════════════════════════
             VISTA: RESUMEN DE ORO
             ════════════════════════════════════════════ */
          <>
            {/* COLUMNA 1: Los 3 Fijos + Top 3 Oportunidades de Puesto */}
            <div className="space-y-6">
              {/* LOS 3 FIJOS */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl">
                <h3 className="flex items-center gap-2 text-yellow-500 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                  <Star size={14} /> Los 3 Fijos
                </h3>
                <div className="space-y-3">
                  {fijos.map((fijo, i) => (
                    <div key={i} className="bg-slate-950 p-3 rounded-xl border border-yellow-900/30">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-500 font-black text-[10px]">CARRERA {fijo.carrera}</span>
                        <span className="bg-yellow-600 text-white text-[8px] px-1 rounded font-bold">{fijo.estado}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-white font-bold text-sm uppercase">{fijo.ejemplar}</span>
                        <span className="text-yellow-500 font-black text-sm">{fijo.probabilidad}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* TOP 3 OPORTUNIDADES DE PUESTO */}
              {topPuestos.length > 0 && (
                <div className="bg-slate-900 border border-orange-800/50 p-5 rounded-3xl">
                  <h3 className="flex items-center gap-2 text-orange-400 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                    <TrendingUp size={14} /> Top 3 Oportunidades de Puesto
                  </h3>
                  <div className="space-y-3">
                    {topPuestos.map((item, i) => (
                      <div key={i} className="bg-slate-950 p-3 rounded-xl border border-orange-900/30">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-yellow-500 font-black text-[10px] uppercase">#{i + 1} · Carrera {item.carrera}</span>
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${colorPuesto(item.puesto_recomendado)}`}>
                            {puestoBadge(item.puesto_recomendado)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white font-bold text-sm uppercase">{item.ejemplar}</span>
                          <span className="text-green-400 font-black text-sm">{item.odds} · +{item.diferencia}%</span>
                        </div>
                        <p className="text-[10px] text-slate-500 italic mt-1 leading-tight">{item.razon}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ESTRATEGIA MULTICARRERA (PICKS) */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl">
                <h3 className="flex items-center gap-2 text-blue-400 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                  <LayoutGrid size={14} /> Estrategia Multi
                </h3>
                <div className="space-y-2">
                  {Object.entries(estrategia)
                    .filter(([key]) => key.toLowerCase().includes('pick'))
                    .map(([key, val]) => (
                      <div key={key} className="bg-slate-950 p-2 px-3 rounded-xl border border-blue-900/20 flex justify-between items-center">
                        <span className="text-[9px] text-slate-500 font-black uppercase">{key.replace(/_/g, ' ')}</span>
                        <span className="text-[10px] text-blue-400 font-black italic">{val}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* COLUMNA 2: TABLA MAESTRA */}
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
              <h3 className="flex items-center gap-2 text-green-500 font-black mb-6 uppercase text-xs tracking-widest border-b border-slate-800 pb-4">
                <Award size={18} /> Tabla Maestra (Resumen)
              </h3>
              <div className="grid grid-cols-1 gap-4 max-h-[600px] overflow-y-auto pr-2 no-scrollbar">
                {tablaMaestra.map((res, idx) => (
                  <div key={idx} className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
                    <div className="flex justify-between items-center mb-2 border-b border-slate-900/50 pb-1">
                      <div className="text-blue-500 font-black text-[10px] uppercase">Carrera {res.carrera}</div>
                      <div className={`text-[9px] font-bold italic uppercase px-1.5 py-0.5 rounded ${estadoColor(res.estado)}`}>{res.estado}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-[11px] text-white font-bold uppercase italic truncate flex-1">
                          <span className="text-yellow-500 mr-1">1º</span> {res['1era_opcion']}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400 font-bold uppercase italic truncate">
                        <span className="text-slate-600 mr-1">2º</span> {res['2da_opcion']}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* COLUMNA 3: MEJORES JUGADAS */}
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl">
                <h3 className="flex items-center gap-2 text-blue-500 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                  <Target size={14} /> Mejores Jugadas
                </h3>
                <div className="space-y-3">
                  {Object.entries(estrategia)
                    .filter(([key]) => !key.toLowerCase().includes('pick'))
                    .map(([tipo, desc], i) => (
                      <div key={i} className="bg-slate-950 p-3 rounded-xl border border-blue-900/20">
                        <span className="block text-[10px] text-yellow-500 font-black uppercase mb-1">{tipo.replace(/_/g, ' ')}</span>
                        <p className="text-[11px] text-slate-300 italic font-medium">{desc}</p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          /* ════════════════════════════════════════════
             VISTA: DETALLE DE CARRERA
             ════════════════════════════════════════════ */
          <>
            {/* COLUMNA 1: Escenario + Análisis de Puestos */}
            <div className="space-y-6">
              {/* ─── ESCENARIO ─── */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl">
                <h3 className="text-yellow-500 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">Escenario</h3>
                <div className="space-y-2">
                  <div className="bg-slate-950 p-3 rounded-xl flex justify-between items-center">
                    <span className="text-[10px] text-blue-500 font-black uppercase">Tipo</span>
                    <span className="text-sm font-bold text-white text-right">{carreraSel.escenario?.tipo || '—'}</span>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl flex justify-between items-center">
                    <span className="text-[10px] text-blue-500 font-black uppercase">Distancia</span>
                    <span className="text-sm font-bold text-white text-right">{carreraSel.escenario?.distancia || '—'}</span>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl flex justify-between items-center">
                    <span className="text-[10px] text-blue-500 font-black uppercase">Superficie</span>
                    <span className="text-sm font-bold text-white text-right">{carreraSel.escenario?.superficie || '—'}</span>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl flex justify-between items-center">
                    <span className="text-[10px] text-blue-500 font-black uppercase">Condición</span>
                    <span className="text-sm font-bold text-white text-right">{carreraSel.escenario?.condicion || '—'}</span>
                  </div>
                  {carreraSel.escenario?.pars && (
                    <div className="bg-slate-950 p-3 rounded-xl">
                      <span className="block text-[10px] text-yellow-500 font-black uppercase mb-2">Pars</span>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-slate-900 rounded-lg p-2">
                          <span className="block text-[9px] text-green-400 font-black">E1</span>
                          <span className="block text-sm font-mono font-bold text-white">{carreraSel.escenario.pars.E1}</span>
                        </div>
                        <div className="bg-slate-900 rounded-lg p-2">
                          <span className="block text-[9px] text-green-400 font-black">E2</span>
                          <span className="block text-sm font-mono font-bold text-white">{carreraSel.escenario.pars.E2}</span>
                        </div>
                        <div className="bg-slate-900 rounded-lg p-2">
                          <span className="block text-[9px] text-purple-400 font-black">LP</span>
                          <span className="block text-sm font-mono font-bold text-white">{carreraSel.escenario.pars.LP}</span>
                        </div>
                        <div className="bg-slate-900 rounded-lg p-2">
                          <span className="block text-[9px] text-orange-400 font-black">SPD</span>
                          <span className="block text-sm font-mono font-bold text-white">{carreraSel.escenario.pars.SPD}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`text-[10px] font-black uppercase px-3 py-1.5 rounded ${estadoColor(carreraSel.estado_carrera)} inline-block`}>
                    {carreraSel.estado_carrera || '—'}
                  </div>
                </div>
              </div>

              {/* ─── ANÁLISIS DE PUESTOS (en CARDS) ─── */}
              {carreraSel.analisis_puestos && (
                <div className="bg-slate-900 border border-indigo-800/50 p-5 rounded-3xl">
                  <h3 className="flex items-center gap-2 text-indigo-400 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                    <Award size={14} /> Análisis de Puestos
                  </h3>

                  {/* Mejor oportunidad — card destacado */}
                  {carreraSel.analisis_puestos.mejor_oportunidad && (
                    <div className="bg-gradient-to-r from-indigo-950 via-slate-950 to-slate-950 border-2 border-yellow-600/40 p-4 rounded-2xl mb-4">
                      <div className="flex items-center gap-1 text-yellow-500 text-[9px] font-black uppercase tracking-widest mb-2">
                        <Star size={12} /> Mejor Oportunidad
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 bg-yellow-500 text-black rounded-lg flex items-center justify-center font-black text-base">
                            {carreraSel.analisis_puestos.mejor_oportunidad.numero}
                          </span>
                          <div>
                            <span className="text-white font-black text-base uppercase block leading-tight">
                              {carreraSel.analisis_puestos.mejor_oportunidad.ejemplar}
                            </span>
                            <span className="text-slate-400 text-[10px] font-bold">
                              Odds: {carreraSel.analisis_puestos.mejor_oportunidad.odds}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-400 font-black text-sm bg-green-400/10 px-2 py-1 rounded">
                            +{carreraSel.analisis_puestos.mejor_oportunidad.diferencia}%
                          </span>
                          <span className={`text-xs font-black px-2 py-1 rounded ${colorPuesto(carreraSel.analisis_puestos.mejor_oportunidad.puesto_recomendado)}`}>
                            {puestoBadge(carreraSel.analisis_puestos.mejor_oportunidad.puesto_recomendado)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cards de cada ejemplar en la tabla de puestos */}
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1 no-scrollbar">
                    {carreraSel.analisis_puestos.tabla?.map((row, idx) => (
                      <div key={idx} className="bg-slate-950 border border-slate-800 rounded-xl p-3 hover:border-indigo-900/50 transition-colors">
                        <div className="flex items-center justify-between gap-2">
                          {/* Número + nombre */}
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="w-7 h-7 bg-slate-800 text-white rounded-lg flex items-center justify-center font-black text-xs shrink-0">
                              {row.numero}
                            </span>
                            <span className="text-white font-bold text-xs uppercase truncate">{row.ejemplar}</span>
                          </div>
                          {/* Odds */}
                          <span className="text-green-400 font-black text-xs shrink-0">{row.odds}</span>
                          {/* Diferencia */}
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded shrink-0 ${diferenciaColor(row.diferencia)}`}>
                            {row.diferencia > 0 ? '+' : ''}{row.diferencia}%
                          </span>
                          {/* Score */}
                          <span className="text-blue-400 font-black text-[10px] shrink-0">S:{row.score_v5}</span>
                          {/* Puesto badge */}
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 ${colorPuesto(row.puesto_recomendado)}`}>
                            {puestoBadge(row.puesto_recomendado)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* COLUMNAS 2+3: Pace + Power Ranking + Recomendaciones */}
            <div className="lg:col-span-2 space-y-6">
              {/* ─── ANÁLISIS DEL PACE ─── */}
              {carreraSel.analisis_pace && (
                <div className="bg-blue-950/20 border border-blue-900 p-5 rounded-3xl">
                  <h4 className="flex items-center gap-2 text-blue-500 text-[10px] font-black uppercase tracking-widest mb-3">
                    <Zap size={14} /> Análisis del Pace
                  </h4>
                  <p className="text-xs text-white leading-relaxed italic font-medium bg-slate-950/50 p-3 rounded-xl border border-blue-900/30">
                    {carreraSel.analisis_pace}
                  </p>
                </div>
              )}

              {/* ─── POWER RANKING ─── */}
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
                <h3 className="text-white font-black mb-4 uppercase text-xs flex items-center gap-2 tracking-widest">
                  <Star size={16} className="text-yellow-500" /> Power Ranking
                </h3>
                <div className="space-y-3">
                  {carreraSel.power_rankings?.map((data, idx) => {
                    const esDebutante = data.prime_power === null || data.prime_power === undefined;
                    return (
                      <div key={idx} className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
                        <div className="flex items-center gap-4">
                          <span className="w-10 h-10 bg-white text-black rounded-lg flex items-center justify-center font-black text-xl shrink-0">
                            {data.numero}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center gap-2">
                              <span className="font-black uppercase text-yellow-500 text-base sm:text-lg truncate">{data.ejemplar}</span>
                              <span className="text-blue-500 font-mono font-black text-sm whitespace-nowrap shrink-0">
                                {esDebutante ? '—' : `PP ${data.prime_power}`}
                              </span>
                            </div>
                            <p className="text-slate-400 text-xs italic mt-1 leading-relaxed">{data.comentario}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ─── RECOMENDACIONES ─── */}
              {carreraSel.recomendaciones && (
                <div className="bg-slate-900 border-2 border-green-900 p-5 rounded-3xl">
                  <h3 className="flex items-center gap-2 text-green-400 font-black mb-4 uppercase text-xs tracking-widest border-b border-slate-800 pb-2">
                    <Target size={16} /> Jugadas Recomendadas
                  </h3>
                  <div className="space-y-3">
                    {carreraSel.recomendaciones.win && (
                      <div className="bg-slate-950 p-3 rounded-xl border border-green-900/30">
                        <span className="text-[10px] text-green-500 font-black uppercase mb-1 block">Ganador</span>
                        <span className="text-lg font-black text-white">{carreraSel.recomendaciones.win}</span>
                      </div>
                    )}
                    {carreraSel.recomendaciones.exacta && (
                      <div className="bg-slate-950 p-3 rounded-xl border border-green-900/30">
                        <span className="text-[10px] text-green-500 font-black uppercase mb-1 block">Exacta</span>
                        <span className="text-base font-mono font-bold text-blue-400">{carreraSel.recomendaciones.exacta}</span>
                      </div>
                    )}
                    {carreraSel.recomendaciones.trifecta && (
                      <div className="bg-slate-950 p-3 rounded-xl border border-green-900/30">
                        <span className="text-[10px] text-green-500 font-black uppercase mb-1 block">Trifecta</span>
                        <span className="text-base font-mono font-bold text-purple-400">{carreraSel.recomendaciones.trifecta}</span>
                      </div>
                    )}
                    {carreraSel.recomendaciones.superfecta && (
                      <div className="bg-slate-950 p-3 rounded-xl border border-green-900/30">
                        <span className="text-[10px] text-green-500 font-black uppercase mb-1 block">Superfecta</span>
                        <span className="text-base font-mono font-bold text-yellow-500">{carreraSel.recomendaciones.superfecta}</span>
                      </div>
                    )}
                    {carreraSel.recomendaciones.daily_double && (
                      <div className="bg-slate-950 p-3 rounded-xl border border-green-900/30">
                        <span className="text-[10px] text-green-500 font-black uppercase mb-1 block">Daily Double</span>
                        <span className="text-base font-mono font-bold text-orange-400">{carreraSel.recomendaciones.daily_double}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
